import { CircularProgress, FormControl, Grid, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { DELETE_COMMENT_BY_ID, getComments } from "../GQL/queries";
import { Comment } from "../Generated/avroGraphqlDuty";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import AddIcon from "@mui/icons-material/AddCircleOutline";
import { State } from "./PilotDutyPage";

interface Props {
    dutyId: number;
    PilotDutyState: State;
    setPilotDutyState(arg0: State): void;
}

interface LocalState {
    Comments: Comment[];
    ShowDeleteDialog: boolean;
    selectedCommentId: number | null;
}

export default function CommentSection(props: Props): JSX.Element {
    const [localState, setLocalState] = useState<LocalState>({
        Comments: [],
        ShowDeleteDialog: false,
        selectedCommentId: null,
    });

    const { error, data, loading } = useQuery(getComments, {
        variables: { commentableModel: "Duty", commentableId: props.dutyId },
        notifyOnNetworkStatusChange: true,
    });

    const [deleteCommentMutation] = useMutation(DELETE_COMMENT_BY_ID, {
        onCompleted: (data) => {
            if (data.deleteCommentById) setLocalState({ ...localState, ShowDeleteDialog: false, selectedCommentId: null });
        },
        refetchQueries: [getComments],
        onError(error, _clientOptions) {
            console.log(`ON DELETE COMMENT ERROR = ${JSON.stringify(error)}`);
        },
    });

    useEffect(() => {
        if (loading === false && data) setLocalState({ ...localState, Comments: data.getComments });
    }, [loading, data]);

    function handleOnClickDeleteFromDialog() {
        deleteCommentMutation({
            variables: {
                deleteCommentByIdId: localState.selectedCommentId,
            },
        });
    }

    function handleOnChangeForEdit(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, commentIndex: number) {
        if (e.target.value !== localState.Comments[commentIndex].comment) {
            const updatedComments =
                localState.Comments &&
                localState.Comments.map((l) => (l && l.id === localState.Comments[commentIndex].id ? { ...l, ["comment"]: e.target.value } : l));
            setLocalState({ ...localState, Comments: updatedComments });
        }
    }

    function handleOnBlurForEdit() {
        props.setPilotDutyState({
            ...props.PilotDutyState,
            Comments: localState.Comments,
            HasChanges: true,
        });
    }

    function handleOnClickDelete(commentIndex: number) {
        const comment = localState.Comments[commentIndex];

        if (comment.id < 0) {
            const updatedComments = localState.Comments.filter((c) => c.id !== comment.id);
            setLocalState({ ...localState, Comments: updatedComments });
        } else setLocalState({ ...localState, selectedCommentId: comment.id, ShowDeleteDialog: true });
    }

    function handleOnClickForAdd() {
        const localId = -99 - localState.Comments.length; // creating a local id for the comment
        const addCommentObject = {
            comment: "",
            commentable_id: props.dutyId,
            commentable_model: "Duty",
            id: localId,
        } as Comment;

        setLocalState({
            ...localState,
            Comments: [...localState.Comments, addCommentObject],
        });
    }

    if (error) return <>Error fetching Comments</>;
    return (
        <>
            {loading ? (
                // placeholder text field
                <FormControl fullWidth>
                    <Typography variant="button" fontSize={18} marginLeft={2}>
                        COMMENTS
                    </Typography>
                    <Grid container pr={2} justifyContent={"space-between"} sx={{ p: 1, px: 2 }}>
                        <Grid item xs={11}>
                            <TextField
                                fullWidth
                                label={"COMMENT"}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="start">
                                            <CircularProgress size={25} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item container justifyContent={"center"} alignItems={"center"} xs={1}>
                            <IconButton size={"large"} edge={"end"} color="error" title="DELETE COMMENT">
                                <RemoveCircleOutline />
                            </IconButton>
                        </Grid>
                    </Grid>
                </FormControl>
            ) : (
                <FormControl fullWidth>
                    <Dialog open={localState.ShowDeleteDialog} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
                        <DialogTitle id="alert-dialog-title">{"Caution!"}</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">Are you sure you wish to delete this comment?</DialogContentText>
                            <DialogContentText id="alert-dialog-description" style={{ fontSize: 14, marginTop: 2 }}>
                                Note: This will also delete all the local comments, if any!
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions sx={{ pb: 2, pr: 2 }}>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setLocalState({ ...localState, ShowDeleteDialog: false });
                                }}>
                                Cancel
                            </Button>
                            <Button variant="contained" color={"error"} onClick={handleOnClickDeleteFromDialog}>
                                Confirm
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Typography variant="button" fontSize={18} marginLeft={2}>
                        COMMENTS
                    </Typography>
                    {localState.Comments &&
                        localState.Comments.map((comment, commentIndex) => (
                            <Grid container key={comment.id} pr={2} justifyContent={"space-between"} sx={{ p: 1, px: 2 }}>
                                <Grid item xs={11}>
                                    <TextField
                                        fullWidth
                                        label={"COMMENT"}
                                        value={comment.comment}
                                        onChange={(e) => handleOnChangeForEdit(e, commentIndex)}
                                        onBlur={handleOnBlurForEdit}
                                    />
                                </Grid>
                                <Grid item container justifyContent={"center"} alignItems={"center"} xs={1}>
                                    <IconButton
                                        size={"large"}
                                        edge={"end"}
                                        color="error"
                                        title="DELETE COMMENT"
                                        onClick={() => handleOnClickDelete(commentIndex)}>
                                        <RemoveCircleOutline />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        ))}
                    <Typography>
                        <IconButton
                            size="large"
                            color="success"
                            title="ADD COMMENT"
                            sx={{ ml: 2, mr: 2, justifyContent: "flex-start" }}
                            onClick={handleOnClickForAdd}>
                            <AddIcon />
                        </IconButton>
                    </Typography>
                </FormControl>
            )}
        </>
    );
}
